import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Lesson } from '../types/lesson';
import type { Order } from '../types/order';
import AutoRenewConfirmModal from './AutoRenewConfirmModal';
import DetailSection from './DetailSection';
import { BTN_PRIMARY_SM } from './buttonStyles';

/**
 * Auto renew, on the program screen.
 *
 * It used to sit in the lessons list, appended under a program's row in six
 * places — four in Lessons.tsx and two in LessonOrderPanel — where it read as a
 * lesson that had turned into an ad. It is a decision about the next purchase,
 * so it belongs at the end of the program it renews, once.
 *
 * The gate is unchanged, only moved: never for a recurring plan or an order
 * already opted in, and for an order queued behind another it waits until
 * lesson 1 of that order is delivered, so nobody is asked to pre-pay for a
 * third program before starting their second.
 *
 * The confirmed state carries the only visible route to cancelling. It came
 * across with the CTA rather than being dropped, because /account?tab=billing
 * is not somewhere a player thinks to look for it.
 */

/**
 * Rules:
 * - Never for a recurring plan; the backend renews those on its own.
 * - Never once opted in; the next order is already recorded.
 * - For a queued order (appearsAfterOrderId), only after lesson 1 is delivered.
 * - Otherwise, show it.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function shouldShowAutoRenewCta(order: Order, orderLessons: Lesson[]): boolean {
  if (order.isRecurringPlan || order.autoRenewOptedIn) return false;
  if (order.appearsAfterOrderId) {
    return orderLessons.some((l) => l.lessonNumber === 1 && l.status === 'delivered');
  }
  return true;
}

export default function AutoRenewSection({
  order, orderLessons, className = '',
}: {
  order: Order;
  orderLessons: Lesson[];
  className?: string;
}) {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  if (order.type !== 'training_program') return null;

  if (order.autoRenewOptedIn && !order.isRecurringPlan) {
    return (
      <DetailSection className={className}>
        <div className="flex items-center justify-between gap-3">
          <span className="text-[15px] leading-[20px] text-[#1C1C1C]">Auto renewal confirmed</span>
          <button
            data-testid="cancel-auto-renew-link"
            onClick={() => navigate('/account?tab=billing')}
            className="shrink-0 text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)] hover:text-[#1C1C1C] bg-transparent border-none p-0 cursor-pointer transition-colors"
          >
            Cancel
          </button>
        </div>
      </DetailSection>
    );
  }

  if (!shouldShowAutoRenewCta(order, orderLessons)) return null;

  return (
    <>
      <DetailSection className={className}>
        <div className="flex items-center justify-between gap-3">
          <span className="text-[15px] leading-[20px] text-[#1C1C1C]">Continue with the same program?</span>
          <button
            data-testid="auto-renew-btn"
            onClick={() => setShowModal(true)}
            className={`${BTN_PRIMARY_SM} shrink-0`}
          >
            Auto Renew
          </button>
        </div>
      </DetailSection>
      {showModal && (
        <AutoRenewConfirmModal
          order={order}
          onClose={() => setShowModal(false)}
          onConfirmed={() => setShowModal(false)}
        />
      )}
    </>
  );
}
